import React, { useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { sprintAPI } from '../lib/api';
import { toast } from 'sonner';
import { Zap, ArrowLeft, Timer, Target, Brain, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

// Fun loading messages
const LOADING_MESSAGES = [
  { text: "Crafting unique clues just for you...", icon: Sparkles },
  { text: "Warming up the trivia engine...", icon: Brain },
  { text: "Shuffling the knowledge deck...", icon: Zap },
  { text: "Almost ready to test your skills...", icon: Target },
];

export default function Sprint() {
  useSEO('Sprint Mode | JepPrep', 'Test your speed and accuracy with timed trivia sprints. Answer 10 questions as fast as you can.');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category') || '';
  const numParam = searchParams.get('num') || '25';
  const [numQuestions, setNumQuestions] = useState(numParam);
  const [difficulty, setDifficulty] = useState('adult');
  const [category] = useState(categoryParam);
  const [isStarting, setIsStarting] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);

  const handleStart = async () => {
    setIsStarting(true);
    setLoadingProgress(0);
    
    // Animate progress and cycle messages
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => Math.min(prev + 15, 90));
    }, 500);
    
    const messageInterval = setInterval(() => {
      setLoadingMessage(prev => {
        const currentIdx = LOADING_MESSAGES.findIndex(m => m.text === prev.text);
        return LOADING_MESSAGES[(currentIdx + 1) % LOADING_MESSAGES.length];
      });
    }, 1200);
    
    try {
      const payload = {
        num_questions: parseInt(numQuestions),
        time_per_question: 15,
        difficulty
      };
      if (category) payload.category = category;
      
      const response = await sprintAPI.start(payload);
      
      clearInterval(progressInterval);
      clearInterval(messageInterval);
      setLoadingProgress(100);
      
      // Small delay to show 100% completion
      setTimeout(() => {
        navigate(`/sprint/${response.data.session_id}`, { 
          state: { 
            session: response.data,
            currentQuestion: response.data.current_question,
            totalQuestions: response.data.total_questions 
          } 
        });
      }, 300);
    } catch (error) {
      clearInterval(progressInterval);
      clearInterval(messageInterval);
      toast.error('Failed to start Sprint session');
      setIsStarting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Navigation */}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-gray-300">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <img src={LOGO_URL} alt="JepPrep" className="h-8 ml-4" />
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto max-w-lg px-4 py-12">
        <AnimatePresence mode="wait">
          {isStarting ? (
            /* Loading Screen */
            <motion.div
              key="loading"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardContent className="p-8">
                  <div className="text-center space-y-6">
                    {/* Animated Icon */}
                    <motion.div 
                      className="w-20 h-20 mx-auto bg-gradient-to-br from-[hsl(var(--brand-gold-500))] to-[hsl(var(--brand-gold-400))] rounded-2xl flex items-center justify-center"
                      animate={{ 
                        rotate: [0, 5, -5, 0],
                        scale: [1, 1.05, 1]
                      }}
                      transition={{ 
                        duration: 2, 
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      {React.createElement(loadingMessage.icon, {
                        className: "w-10 h-10 text-[hsl(var(--brand-blue-900))]"
                      })}
                    </motion.div>
                    
                    {/* Title */}
                    <div>
                      <h2 className="text-xl font-display text-white mb-2">
                        Preparing Your Sprint
                      </h2>
                      <motion.p 
                        key={loadingMessage.text}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-gray-400"
                      >
                        {loadingMessage.text}
                      </motion.p>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="space-y-2">
                      <Progress 
                        value={loadingProgress} 
                        className="h-2 bg-[hsl(var(--accent))]"
                      />
                      <p className="text-xs text-gray-500">
                        {loadingProgress < 100 ? 'Setting up clues...' : 'Ready!'}
                      </p>
                    </div>
                    
                    {/* Fun Fact */}
                    <div className="bg-[hsl(var(--accent))] rounded-lg p-4 text-left">
                      <p className="text-xs text-gray-400 mb-1">Did you know?</p>
                      <p className="text-sm text-gray-300">
                        Each clue is uniquely crafted to give you a fresh challenge every time!
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            /* Setup Screen */
            <motion.div
              key="setup"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 mx-auto bg-[hsl(var(--brand-blue-800))] rounded-xl flex items-center justify-center mb-4">
                    <Zap className="w-8 h-8 text-[hsl(var(--brand-gold-500))]" />
                  </div>
                  <CardTitle className="text-2xl font-display text-white">Sprint Mode</CardTitle>
                  <CardDescription className="text-gray-400">
                    Rapid-fire trivia training. Answer as many questions as you can.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Category Badge (when pre-selected from Clue Explorer) */}
                  {category && (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 flex items-center gap-3" data-testid="sprint-category-badge">
                      <Target className="w-5 h-5 text-emerald-400 shrink-0" />
                      <div>
                        <p className="text-emerald-400 text-sm font-medium">Focused Practice</p>
                        <p className="text-white text-base font-semibold">{category}</p>
                      </div>
                    </div>
                  )}

                  {/* Number of Questions */}
                  <div className="space-y-2">
                    <Label className="text-gray-300">Number of Questions</Label>
                    <div className="grid grid-cols-3 gap-3">
                      <Button
                        type="button"
                        variant={numQuestions === '15' ? 'default' : 'outline'}
                        onClick={() => setNumQuestions('15')}
                        className={numQuestions === '15' ? 'bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white' : 'border-[hsl(var(--border))] text-gray-300'}
                        data-testid="sprint-15-questions"
                      >
                        15
                      </Button>
                      <Button
                        type="button"
                        variant={numQuestions === '25' ? 'default' : 'outline'}
                        onClick={() => setNumQuestions('25')}
                        className={numQuestions === '25' ? 'bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white' : 'border-[hsl(var(--border))] text-gray-300'}
                        data-testid="sprint-25-questions"
                      >
                        25
                      </Button>
                      <Button
                        type="button"
                        variant={numQuestions === '50' ? 'default' : 'outline'}
                        onClick={() => setNumQuestions('50')}
                        className={numQuestions === '50' ? 'bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white' : 'border-[hsl(var(--border))] text-gray-300'}
                        data-testid="sprint-50-questions"
                      >
                        50
                      </Button>
                    </div>
                  </div>

                  {/* Difficulty */}
                  <div className="space-y-2">
                    <Label className="text-gray-300">Difficulty</Label>
                    <Select value={difficulty} onValueChange={setDifficulty}>
                      <SelectTrigger className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white" data-testid="sprint-difficulty-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                        <SelectItem value="college" className="text-white">College</SelectItem>
                        <SelectItem value="adult" className="text-white">Adult</SelectItem>
                        <SelectItem value="masters" className="text-white">Masters</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Info */}
                  <div className="bg-[hsl(var(--accent))] rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <Timer className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                      <span className="text-white">20 seconds per question</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Target className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                      <span className="text-white">Category performance analysis after</span>
                    </div>
                  </div>

                  <Button
                    onClick={handleStart}
                    disabled={isStarting}
                    className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white py-6 text-lg"
                    data-testid="start-sprint-button"
                  >
                    Start Sprint
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
